import React, { useEffect, useRef, useState } from "react";

type DateTimePickerProps = {
  name: string;
  value: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  inputClassName?: string;
  label?: string;
};

const DateTimePicker: React.FC<DateTimePickerProps> = (props) => {
  const { value, onChange, disabled, required, inputClassName } = props;
  const [valueDate, setValueDate] = useState("");
  const [valueHours, setValueHours] = useState("09");
  const [valueMinutes, setValueMinutes] = useState("00");
  const [valueAMPM, setValueAMPM] = useState("AM");

  const refDate = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (value) {
      const valueDateTime = new Date(value);
      // format date
      const offset = valueDateTime.getTimezoneOffset();
      const valueDateTimeOffset = new Date(
        valueDateTime.getTime() - offset * 60 * 1000,
      );
      const valueDate = valueDateTimeOffset.toISOString().split("T")[0];
      setValueDate(valueDate);
      // format hours
      let valueHoursFormatted =
        valueDateTime.getHours() > 12
          ? (valueDateTime.getHours() - 12).toString()
          : valueDateTime.getHours().toString();

      if (valueHoursFormatted.length === 1) {
        valueHoursFormatted = "0" + valueHoursFormatted;
      }
      setValueHours(valueHoursFormatted);
      // format mins
      let valueMinutesFormatted = valueDateTime.getMinutes().toString();
      if (valueMinutesFormatted.length === 1) {
        valueMinutesFormatted = "0" + valueMinutesFormatted;
      }
      setValueMinutes(valueMinutesFormatted);
      // format AM PM
      let valueAMPMFormatted = valueDateTime.getHours() > 12 ? "PM" : "AM";
      setValueAMPM(valueAMPMFormatted);
    } else {
      setValueDate("");
    }
  }, [value]);

  const handleOnChange = (
    newDate: string,
    newHours: string,
    newMinutes: string,
    newAMPM: string,
  ) => {
    refDate.current?.reportValidity();
    if (newDate) {
      if (refDate.current?.checkValidity()) {
        const newValue = new Date(newDate);
        let hours = parseInt(newHours);
        hours = newAMPM === "PM" ? hours + 12 : hours;
        newValue.setHours(hours);
        newValue.setMinutes(parseInt(newMinutes));
        if (onChange) {
          onChange(newValue.toISOString());
        }
      }
    } else {
      if (onChange) {
        onChange("");
      }
    }
  };

  const handleDateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    handleOnChange(value, valueHours, valueMinutes, valueAMPM);
  };

  const handleHoursInputChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    handleOnChange(valueDate, value, valueMinutes, valueAMPM);
  };

  const handleMinutesInputChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const value = e.target.value;
    handleOnChange(valueDate, valueHours, value, valueAMPM);
  };

  const handleAMPMInputChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    handleOnChange(valueDate, valueHours, valueMinutes, value);
  };

  return (
    <div className="mb-4">
      {props.label && (
        <div className="mb-2 block">
          <label
            htmlFor={props.name}
            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
          >
            {props.label}{" "}
            {required ? <span className="text-red-500">*</span> : ""}
          </label>
        </div>
      )}
      <div className="flex flex-row">
        <input
          ref={refDate}
          type="date"
          name={props.name}
          value={valueDate}
          onChange={handleDateInputChange}
          className={inputClassName}
          disabled={disabled}
          required={required}
        />
        <select
          value={valueHours}
          onChange={handleHoursInputChange}
          className={inputClassName}
          disabled={disabled}
          required={required}
        >
          <option value="01">01</option>
          <option value="02">02</option>
          <option value="03">03</option>
          <option value="04">04</option>
          <option value="05">05</option>
          <option value="06">06</option>
          <option value="07">07</option>
          <option value="08">08</option>
          <option value="09">09</option>
          <option value="10">10</option>
          <option value="11">11</option>
          <option value="12">12</option>
        </select>
        <select
          value={valueMinutes}
          onChange={handleMinutesInputChange}
          className={inputClassName}
          disabled={disabled}
          required={required}
        >
          <option value="00">00</option>
          <option value="15">15</option>
          <option value="30">30</option>
          <option value="45">45</option>
        </select>
        <select
          value={valueAMPM}
          onChange={handleAMPMInputChange}
          className={inputClassName}
          disabled={disabled}
          required={required}
        >
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
    </div>
  );
};

export default DateTimePicker;
